import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import VehiclesPage from "@/pages/VehiclesPage";
import VehicleDetailPage from "@/pages/VehicleDetailPage";
import DriversPage from "@/pages/DriversPage";
import StoresPage from "@/pages/StoresPage";
import RoutePlannerPage from "@/pages/RoutePlannerPage";
import RouteSheetPage from "@/pages/RouteSheetPage";
import WeeklyReportPage from "@/pages/WeeklyReportPage";
import InsurancePage from "@/pages/InsurancePage";
import SuppliersPage from "@/pages/SuppliersPage";
import DriverRoutePage from "@/pages/DriverRoutePage";
import CostsPage from "@/pages/CostsPage";
import FinesPage from "@/pages/FinesPage";
import UsersPage from "@/pages/UsersPage";
import ChargesPage from "@/pages/ChargesPage";
import CompaniesPage from "@/pages/CompaniesPage";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground font-mono text-sm tracking-widest animate-pulse">LOADING…</div>
      </div>
    );
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function NoAccess() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center p-8" data-testid="no-access-panel">
      <div className="text-center">
        <div className="font-heading font-black text-3xl tracking-tighter mb-2">NO ACCESS</div>
        <p className="text-muted-foreground text-sm">You don't have permission to view this section. Ask your administrator.</p>
      </div>
    </div>
  );
}

function Section({ section, children }) {
  const { can, isAdmin } = useAuth();
  const allowed = section === "users" ? isAdmin : can(section);
  return allowed ? children : <NoAccess />;
}

function HomeRoute() {
  const { can, isAdmin } = useAuth();
  if (can("dashboard")) return <DashboardPage />;
  const order = ["vehicles", "drivers", "stores", "routes", "costs", "fines", "insurance", "suppliers"];
  const first = order.find((s) => can(s));
  if (first) return <Navigate to={`/${first}`} replace />;
  if (isAdmin) return <Navigate to="/users" replace />;
  return <NoAccess />;
}

function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster theme={theme} position="top-right" richColors />;
}

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/route-sheet/:id"
            element={
              <Protected>
                <Section section="routes"><RouteSheetPage /></Section>
              </Protected>
            }
          />
          <Route
            path="/weekly-report"
            element={
              <Protected>
                <Section section="dashboard"><WeeklyReportPage /></Section>
              </Protected>
            }
          />
          <Route path="/r/:token" element={<DriverRoutePage />} />
          <Route
            path="/"
            element={
              <Protected>
                <Layout />
              </Protected>
            }
          >
            <Route index element={<HomeRoute />} />
            <Route path="vehicles" element={<Section section="vehicles"><VehiclesPage /></Section>} />
            <Route path="charges" element={<Section section="vehicles"><ChargesPage /></Section>} />
            <Route path="companies" element={<Section section="vehicles"><CompaniesPage /></Section>} />
            <Route path="vehicles/:id" element={<Section section="vehicles"><VehicleDetailPage /></Section>} />
            <Route path="drivers" element={<Section section="drivers"><DriversPage /></Section>} />
            <Route path="stores" element={<Section section="stores"><StoresPage /></Section>} />
            <Route path="routes" element={<Section section="routes"><RoutePlannerPage /></Section>} />
            <Route path="costs" element={<Section section="costs"><CostsPage /></Section>} />
            <Route path="fines" element={<Section section="fines"><FinesPage /></Section>} />
            <Route path="insurance" element={<Section section="insurance"><InsurancePage /></Section>} />
            <Route path="suppliers" element={<Section section="suppliers"><SuppliersPage /></Section>} />
            <Route path="users" element={<Section section="users"><UsersPage /></Section>} />
          </Route>
        </Routes>
      </BrowserRouter>
      <ThemedToaster />
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
